import { type ReactNode, useEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { colors, card, btnBase } from './styles'
import type { CSSProperties } from 'react'

interface ModalProps {
  readonly isOpen: boolean
  readonly onClose: () => void
  readonly title: string
  readonly children: ReactNode
  readonly width?: string
  readonly showCloseButton?: boolean
}

const overlay: CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0, 0, 0, 0.5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 9999,
}

const content = (width: string): CSSProperties => ({
  ...card,
  width,
  maxWidth: '90vw',
  maxHeight: '85vh',
  overflow: 'auto',
  padding: 0,
})

const header: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '1rem 1.25rem',
  borderBottom: `1px solid ${colors.border}`,
}

const titleStyle: CSSProperties = {
  fontSize: '1.125rem',
  fontWeight: 600,
  color: colors.text,
  margin: 0,
}

const closeBtn: CSSProperties = {
  ...btnBase,
  background: 'none',
  padding: '0.25rem 0.5rem',
  fontSize: '1.25rem',
  color: colors.textMuted,
  lineHeight: 1,
}

const body: CSSProperties = {
  padding: '1.25rem',
}

export function Modal({ isOpen, onClose, title, children, width = '480px', showCloseButton = true }: ModalProps) {
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
      }
    },
    [onClose],
  )

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown)
      return () => document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen, handleKeyDown])

  if (!isOpen) return null

  return createPortal(
    <div style={overlay} onClick={onClose} data-testid="modal-overlay">
      <div style={content(width)} onClick={(e) => e.stopPropagation()} data-testid="modal-content">
        <div style={header}>
          <h2 style={titleStyle}>{title}</h2>
          {showCloseButton && (
            <button style={closeBtn} onClick={onClose} aria-label="Close" data-testid="modal-close">
              &times;
            </button>
          )}
        </div>
        <div style={body}>{children}</div>
      </div>
    </div>,
    document.body,
  )
}
